import { useMemo } from 'react';
import { useLoaderData } from 'react-router';

import { useEnquiryModal } from '~/modules/enquiry/hooks/use-enquiry-modal';
import type { loader } from '~/routes/_app.product.$urlSegment';

/**
 * The standard product enquiry modal, pre-configured from the product loader
 * (professional, branch options, primary CTA label, product title + image).
 * Shared by the gallery photo viewer, the downloads row and the spec
 * accordion so the config lives in one place.
 */
export const useProductEnquiryModal = () => {
    const { product, branchOptions, primaryCTA } =
        useLoaderData<typeof loader>();
    const professional = product.Professional;

    // Memoised so the returned `openEnquiryModal` keeps a stable identity across
    // renders — an unstable config would churn any island mode that closes over
    // it (re-registering every section each render).
    const data = useMemo(
        () => ({
            professional: {
                ID: professional.ID,
                Title: professional.Title,
                branchOptions,
                CompanyLogo: professional.CompanyLogo ?? '',
                LogoBackgroundColor:
                    professional.LogoBackgroundColor ?? 'white',
                CustomEnquiryMessage:
                    professional.CustomEnquiryMessage ?? undefined,
            },
            itemType: 'product' as const,
            siteTreeID: product.ID,
            ctaLabel: primaryCTA.primaryCTALabelTitleCase,
            includeBranch: branchOptions.length > 0,
            itemTitle: product.Title,
            itemImage:
                product.LibraryImages?.[0]?.LibraryImageFile?.PhotoTile ??
                undefined,
        }),
        [product, professional, branchOptions, primaryCTA],
    );

    return useEnquiryModal(data);
};
